/* The record-keeping queue's LIST (Tier-2 D1, Ale 16:4x 2026-09-05 —
 * "record-keeping queue as list + detail; J/K, one verb canon; card
 * content stays the locked design").
 *
 * The list REPLACES the sequential pager. The B6 audit measured the old
 * shape: 94 segments behind Prev/Next is 94 clicks to reach the last
 * one, and the five decisions that carry the day sat behind 88 bookings.
 * Here every segment is one line, the decisions are the first section,
 * and the cursor walks the whole queue with J / K.
 *
 * The line says only what the queue payload actually carries — the
 * segment sentence (`title`), the document it came from, its gate, and
 * the fund when the officer is looking at every fund at once. The card
 * on the right is unchanged: it is the locked design, and it stays.
 *
 * Mock of record: ux-audit-2026-09-04/mocks/rk-stream-list-detail.png.
 */

import type { DeckQueueItem } from './types'
import { rowLabel, rowSource, STAGE_LABEL, type Stage } from './segments'

/** The gutter mark is the STAGE, the only thing the queue payload knows
 *  about a segment before its deck is read — a question for a decision,
 *  the look-through arrow for a walk, a break dot for a reconcile, a
 *  plain dot for a booking. Object colours belong to the card, which
 *  has read the objects. */
function GutterMark({ stage }: { stage: string }) {
  if (stage === 'deciding')
    return (
      <span className="qdot" aria-hidden="true">
        ?
      </span>
    )
  if (stage === 'walking')
    return (
      <span className="wkg" aria-hidden="true">
        ↧
      </span>
    )
  return <span className={stage === 'reconciling' ? 'rcdot' : 'bkdot'} aria-hidden="true" />
}

interface Props {
  decks: DeckQueueItem[]
  selectedId: string | null
  /** Row click and the row's own Enter — select AND move into the card. */
  onOpen: (deckId: string, index: number) => void
  /** Fund name for a deck, when the queue spans every fund. */
  fundNameOf: (d: DeckQueueItem) => string | null
  listRef: React.RefObject<HTMLDivElement | null>
}

export default function RkSegmentList({ decks, selectedId, onOpen, fundNameOf, listRef }: Props) {
  const counts = decks.reduce<Record<string, number>>((acc, d) => {
    acc[d.stage] = (acc[d.stage] ?? 0) + 1
    return acc
  }, {})

  let last: string | null = null
  return (
    <div className="rklist" data-testid="rk-list" ref={listRef} tabIndex={-1}>
      <div className="rkrows">
        {decks.map((d, i) => {
          const head = d.stage === last ? null : d.stage
          last = d.stage
          const sel = selectedId === d.deck_id
          const fund = fundNameOf(d)
          const source = rowSource(d)
          return (
            <div key={d.deck_id} className="rkrowwrap">
              {head ? (
                <div
                  className={`rksec${head === 'deciding' || head === 'walking' ? ' wine' : ''}`}
                  data-testid="rk-section"
                >
                  {STAGE_LABEL[head as Stage] ?? head} · {counts[head] ?? 0}
                </div>
              ) : null}
              <button
                type="button"
                className={`rkrow${sel ? ' on' : ''}`}
                data-testid="rk-row"
                data-deck-id={d.deck_id}
                data-stage={d.stage}
                aria-current={sel ? 'true' : undefined}
                onClick={() => onOpen(d.deck_id, i)}
              >
                <span className="g">
                  <GutterMark stage={d.stage} />
                </span>
                <span className="tx">
                  <span className="nm">{rowLabel(d)}</span>
                  {source || d.gated || fund ? (
                    <span className="l2">
                      <span className="sb">{source}</span>
                      <span className="rt">
                        {d.gated ? (
                          <span className="gatedchip" data-testid="rk-row-gated">
                            gated
                          </span>
                        ) : null}
                        {fund ? (
                          <span className="fundtag" data-testid="rk-row-fund">
                            {fund}
                          </span>
                        ) : null}
                      </span>
                    </span>
                  ) : null}
                </span>
              </button>
            </div>
          )
        })}
      </div>
      {/* The keys that actually work here — nothing else is offered. */}
      <div className="rkkeys">
        <kbd>J</kbd> / <kbd>K</kbd> move · <kbd>Enter</kbd> opens · <kbd>Esc</kbd> back
      </div>
    </div>
  )
}
